import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Per-user default equipment type for the LR Direct post form.
 * Stored on `profiles.default_equipment_type`.
 */
export function useDefaultEquipment() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [defaultEquipment, setDefaultEquipment] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("default_equipment_type")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled) return;
      setDefaultEquipment(((data as any)?.default_equipment_type as string) || null);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const saveDefault = useCallback(async (equipmentType: string) => {
    if (!userId || !equipmentType) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ default_equipment_type: equipmentType } as any)
      .eq("id", userId);
    setSaving(false);
    if (error) {
      toast.error("Could not save default equipment");
      return;
    }
    setDefaultEquipment(equipmentType);
    setJustSaved(true);
    toast.success("Saved as your default equipment");
    setTimeout(() => setJustSaved(false), 4000);
  }, [userId]);

  return { defaultEquipment, loaded, saving, justSaved, saveDefault };
}
